import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { getApiUrl } from '../config/api.config';

const VerifyEmail = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');
	const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
	const [message, setMessage] = useState('');

	useEffect(() => {
		if (!token) {
			setStatus('error');
			setMessage('Missing verification link. Please use the link from your email.');
			return;
		}
		// Decode in case the URL was encoded by the email client or browser
		const tokenToSend = (() => {
			try {
				return decodeURIComponent(token.trim());
			} catch {
				return token.trim();
			}
		})();

		const verify = async () => {
			try {
				await axios.get(getApiUrl('auth/verify-email'), { params: { token: tokenToSend } });
				setStatus('success');
				setMessage('Your email has been verified. You can now log in.');
			} catch (err) {
				setStatus('error');
				setMessage(
					err.response?.data?.message ||
						err.response?.data?.error ||
						err.message ||
						'Verification failed. The link may be invalid or expired.'
				);
			}
		};

		verify();
	}, [token]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full">
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
					<div className="px-10 py-12">
						<div className="text-center mb-8">
							<div className="flex justify-center items-center mb-6">
								<Logo showText={true} width={80} height={80} className="justify-center" />
							</div>
							<h2 className="text-2xl font-bold text-gray-900 mb-2">Email verification</h2>
						</div>

						{status === 'loading' && (
							<div className="flex flex-col items-center justify-center py-8">
								<svg
									className="animate-spin h-10 w-10 text-blue-600 mb-4"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									/>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								<p className="text-gray-600">Verifying your email...</p>
							</div>
						)}

						{status === 'success' && (
							<div className="space-y-6">
								<div className="bg-green-50 border border-green-200 rounded-lg p-4">
									<div className="flex items-center">
										<svg
											className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										<span className="text-sm font-medium text-green-800">{message}</span>
									</div>
								</div>
								<Link
									to="/login"
									className="block w-full py-3 px-4 text-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg transition duration-200"
								>
									Go to login
								</Link>
							</div>
						)}

						{status === 'error' && (
							<div className="space-y-6">
								<div className="bg-red-50 border border-red-200 rounded-lg p-4">
									<div className="flex items-start">
										<svg
											className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
												clipRule="evenodd"
											/>
										</svg>
										<span className="text-sm text-red-800">{message}</span>
									</div>
								</div>
								<div className="flex flex-col gap-2">
									<Link
										to="/signup"
										className="block w-full py-3 px-4 text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition"
									>
										Sign up again
									</Link>
									<Link
										to="/login"
										className="block w-full py-3 px-4 text-center text-blue-600 hover:text-blue-700 font-medium"
									>
										Back to login
									</Link>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default VerifyEmail;
